// =============================================================================
// Zmienne wydruku testowego — JEDNO miejsce, w którym panel wypełnia to, czego
// żąda katalog wzorów, zanim zleci prawdziwe zadanie.
//
// Powód istnienia (zmierzone 10.09.2026 na produkcji): oba przyciski „Wydruk
// testowy" — w kartotece drukarek i w galerii wzorów — wysyłały
// `variables: {}`, a bramka pustego payloadu (`walidujZmienne` w pakiecie
// `print-templates`) słusznie odbijała to 422-ką `missing_variable`
// („brak wymaganych zmiennych: printed_at"). Bramka działała; wołający nie
// dostarczał. Do tego kartoteka drukarek zlecała ZAWSZE wzór etykiety
// `system.test_page` — także drukarce systemowej (A4 przez sterownik)
// i paragonowej (ESC/POS, dla której ten wzór w ogóle nie deklaruje celu,
// więc kończyło się 422 `format_mismatch`).
//
// Dobór wzoru do drukarki LUSTRZY `celDlaDrukarki` z
// `services/print-core/src/zlecenia.ts`: `system` = sterownik = PDF; sieciowa
// mówi językiem protokołu; `RAW`/brak protokołu nie wskazuje enkodera, więc
// wzoru dla niej NIE MA — zamiast zgadywać oddajemy `null`, a UI mówi to
// wprost. Test `zmienneWydruku.test.ts` sprawdza każdy zwracany slug
// z żywym katalogiem: istnieje, deklaruje ten cel i jego zmienne
// nieopcjonalne są w komplecie.
// =============================================================================

import type { SzablonKatalogu } from './printCoreApi';

/** Minimalny kształt drukarki potrzebny do doboru wzoru — bez zależności od hooka. */
export interface DrukarkaDoTestu {
  name: string;
  type: string;
  protocol: string | null;
}

/** Wzory rodziny `system` — jedna strona testowa na klasę mediów. */
export const SLUG_STRONY_TESTOWEJ = {
  etykieta: 'system.test_page',
  paragon: 'system.test_page_receipt',
  dokument: 'system.test_page_a4',
} as const;

const CELE_ETYKIET: readonly string[] = ['ZPL', 'EPL', 'TSPL'];

/**
 * Cel renderu wynikający z drukarki — kalka `celDlaDrukarki` (print-core).
 * Zwraca `null` dla `RAW` i braku protokołu: plan wzoru nie renderuje na taką
 * drukarkę, więc i strona testowa z katalogu jej nie dotyczy.
 */
export function celDrukarki(d: Pick<DrukarkaDoTestu, 'type' | 'protocol'>): string | null {
  if (d.type === 'system') return 'PDF';
  if (d.protocol === 'PDF') return 'PDF';
  if (d.protocol && CELE_ETYKIET.includes(d.protocol)) return d.protocol;
  if (d.protocol === 'ESCPOS') return 'ESCPOS';
  return null;
}

/**
 * Wzór strony testowej dla drukarki: dokument A4 przez sterownik, paragon dla
 * ESC/POS, etykieta 80×50 dla języków etykietowych. `null` = brak wzoru
 * (drukarka bez protokołu / `RAW`) — wołający ma o tym powiedzieć, nie zgadywać.
 */
export function slugStronyTestowej(d: Pick<DrukarkaDoTestu, 'type' | 'protocol'>): string | null {
  const cel = celDrukarki(d);
  if (cel === 'PDF') return SLUG_STRONY_TESTOWEJ.dokument;
  if (cel === 'ESCPOS') return SLUG_STRONY_TESTOWEJ.paragon;
  if (cel !== null) return SLUG_STRONY_TESTOWEJ.etykieta;
  return null;
}

/**
 * Chwila wydruku w kształcie przykładu z katalogu (`24.08.2026, 14:32`) —
 * `pl-PL` daje dokładnie ten zapis, a strefa jest strefą przeglądarki
 * technika stojącego przy drukarce, czyli tą, którą odczyta z papieru.
 */
export function formatujChwileWydruku(teraz: Date): string {
  return new Intl.DateTimeFormat('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(teraz);
}

/**
 * Wartości żywe strony testowej — komplet `ZMIENNE_TESTOWE` rodziny `system`
 * (`printed_at` wymagana, `printer_name` opcjonalna). Kartoteka drukarek nie
 * ładuje katalogu (czyta wyłącznie przez RLS), więc nazwy są tu wpisane, a
 * zgodność z katalogiem pilnuje test — nie pamięć autora.
 */
export function zmienneStronyTestowej(
  drukarka: Pick<DrukarkaDoTestu, 'name'>,
  teraz: Date = new Date()
): Record<string, unknown> {
  return {
    printed_at: formatujChwileWydruku(teraz),
    printer_name: drukarka.name,
  };
}

/**
 * Zmienne wydruku testowego DOWOLNEGO wzoru z galerii: przykłady z deklaracji
 * katalogu (te same, których używa podgląd — `previewVars`), nadpisane
 * wartościami żywymi tam, gdzie wzór je deklaruje. Etykieta partii wychodzi
 * więc z przykładową partią, a strona testowa z prawdziwą godziną i nazwą
 * drukarki — nigdy z `{}`.
 */
export function zmienneWydrukuTestowego(
  zmienne: NonNullable<SzablonKatalogu['variables']>,
  kontekst: { drukarka: Pick<DrukarkaDoTestu, 'name'>; teraz?: Date }
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const z of zmienne) {
    if (z.example !== undefined) out[z.name] = z.example;
  }
  const zywe = zmienneStronyTestowej(kontekst.drukarka, kontekst.teraz);
  for (const [nazwa, wartosc] of Object.entries(zywe)) {
    if (zmienne.some((z) => z.name === nazwa)) out[nazwa] = wartosc;
  }
  return out;
}
